import { useEffect, useReducer, useRef, useState, useCallback, memo, type ReactNode } from 'react';
import { setControlRendererRef } from './controlRendererRef';
import { EmptyState } from '@packages/ui/layout/EmptyState';
import type {
  AnyBlock,
  ComposeSchema,
  ResolvedAnyBlock,
  ResolvedComposeSchema,
  BlockRendererSharedProps,
  ActionBlock,
} from '../types/blocks';
import { isComposeSchema } from '../types/blocks';
import { getComposeLoader, getComposeVersion, onComposeChanged } from '../registry/loaderRegistry';
import { FormBlockRenderer } from '../renderers/FormBlockRenderer';
import { TableBlockRenderer } from '../renderers/TableBlockRenderer';
import { StackBlockRenderer } from '../renderers/StackBlockRenderer';
import { ActionBlockRenderer } from '../renderers/ActionBlockRenderer';
import { ViewBlockRenderer } from '../renderers/ViewBlockRenderer';
import { TabBlockRenderer } from '../renderers/TabBlockRenderer';
import { AccordionBlockRenderer } from '../renderers/AccordionBlockRenderer';
import { ButtonBlockRenderer } from '../renderers/ButtonBlockRenderer';
import { WizardBlockRenderer } from '../wizard/WizardBlockRenderer';
import { EventScope } from '@packages/block-events/core';
import type { EffectDeclaration, BlockWire } from '@packages/block-events/core';
import { EventScopeProvider } from '@packages/block-events/react';
import { EffectInterpreter, expandWire } from '@packages/block-events/interpreter';

export type ControlRendererBlock =
  | AnyBlock
  | ResolvedAnyBlock
  | ComposeSchema
  | ResolvedComposeSchema;

export interface ControlRendererProps extends BlockRendererSharedProps {
  block?: ControlRendererBlock;
  composeId?: string;
  /** Action-driven rendering — calls this builder action which returns a block tree. */
  actionId?: string;
  /** Reactive handle — pass from `useControlRendererRef()` to read `handle.isLoading` in the parent. */
  handle?: import('./useControlRendererRef').ControlRendererHandle;
}

interface LoadState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  schema: ComposeSchema | null;
  error: string | null;
}

function useComposeSchema(composeId: string | undefined): LoadState {
  const [state, forceUpdate] = useReducer(
    (s: LoadState, next: Partial<LoadState>) => ({ ...s, ...next }),
    { status: 'idle', schema: null, error: null },
  );

  const [composeVersion, setComposeVersion] = useState(getComposeVersion);

  useEffect(() => {
    return onComposeChanged(() => setComposeVersion(getComposeVersion()));
  }, []);

  const loadSchema = useCallback(() => {
    if (!composeId) return;

    const loader = getComposeLoader();
    if (!loader) {
      forceUpdate({
        status: 'error',
        error:
          'No compose loader registered. Make sure @packages/domain is imported ' +
          'somewhere in the app entry point (e.g. import "@packages/domain").',
      });
      return;
    }

    forceUpdate({ status: 'loading', error: null });
    loader(composeId)
      .then((schema) => {
        forceUpdate({ status: 'ready', schema, error: null });
      })
      .catch((err: unknown) => {
        forceUpdate({
          status: 'error',
          error: err instanceof Error ? err.message : String(err),
        });
      });
  }, [composeId]);

  useEffect(() => {
    loadSchema();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composeId, composeVersion]);

  return state;
}

function resolveRootBlock(block: ControlRendererBlock): AnyBlock | ResolvedAnyBlock {
  if (isComposeSchema(block) || (block as any).$type === 'compose') {
    const compose = block as ComposeSchema | ResolvedComposeSchema;
    if (typeof compose.root === 'string') {
      throw new Error(
        '[ControlRenderer] Compose schema root is an unresolved URI. Call loadComposeSchema() first.',
      );
    }
    return compose.root as AnyBlock | ResolvedAnyBlock;
  }

  return block as AnyBlock | ResolvedAnyBlock;
}

export function ControlRenderer(props: ControlRendererProps): JSX.Element {
  const { block: blockProp, composeId, actionId, params, meta, onSuccess, onError, onCancel, handle } = props;

  if (actionId) {
    const actionBlock: ActionBlock = { $type: 'action', $id: `action://${actionId}`, actionId };
    return (
      <ActionIdScopeBoundary actionId={actionId}>
        <ActionBlockRenderer block={actionBlock} params={params} meta={meta} onSuccess={onSuccess} onError={onError} onCancel={onCancel} handle={handle} />
      </ActionIdScopeBoundary>
    );
  }

  const loadState = useComposeSchema(composeId);

  const block: ControlRendererBlock | null = composeId
    ? (loadState.schema ?? null)
    : (blockProp ?? null);

  if (composeId) {
    if (loadState.status === 'loading' || loadState.status === 'idle') {
      return <></>;
    }
    if (loadState.status === 'error' || !block) {
      return (
        <EmptyState
          icon='error'
          title='Failed to load compose schema'
          description={loadState.error ?? `Could not load "${composeId}"`}
          semanticId='controlRendererLoadError'
        />
      );
    }
  }

  if (!block) {
    return (
      <EmptyState
        icon='error'
        title='No schema provided'
        description='ControlRenderer requires either a block object or a composeId.'
        semanticId='controlRendererNoBlock'
      />
    );
  }

  let root: AnyBlock | ResolvedAnyBlock;
  try {
    root = resolveRootBlock(block);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return (
      <EmptyState
        icon='error'
        title='Unable to render compose schema'
        description={message}
        semanticId='controlRendererComposeError'
      />
    );
  }

  const shared: BlockRendererSharedProps = { params, meta, onSuccess, onError, onCancel, handle };
  const schema = isComposeSchema(block) ? block : null;

  return (
    <EventScopeBoundary schema={schema} root={root} shared={shared} />
  );
}

/**
 * Manages the EventScope lifecycle and EffectInterpreter wiring.
 * Separated from ControlRenderer so hooks are never called conditionally.
 */
function EventScopeBoundary({
  schema,
  root,
  shared,
}: {
  schema: ComposeSchema | null;
  root: AnyBlock | ResolvedAnyBlock;
  shared: BlockRendererSharedProps;
}): JSX.Element {
  const scopeRef = useRef<EventScope | null>(null);
  const interpreterRef = useRef<EffectInterpreter | null>(null);

  const schemaEffects = schema?.effects ?? [];
  const wireEffects = collectWireEffects(root);
  const hasEffects = schemaEffects.length > 0 || wireEffects.length > 0;

  if (!scopeRef.current && (hasEffects || schema != null)) {
    scopeRef.current = new EventScope(schema?.$id ?? 'anon');
  }

  useEffect(() => {
    if (!scopeRef.current) return;

    const allEffects = [...schemaEffects, ...wireEffects];
    if (allEffects.length > 0) {
      interpreterRef.current = new EffectInterpreter(scopeRef.current, allEffects);
    }

    return () => {
      interpreterRef.current?.dispose();
      interpreterRef.current = null;
    };
  // Re-wire when schema identity changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema?.$id]);

  useEffect(() => {
    return () => {
      scopeRef.current?.dispose();
      scopeRef.current = null;
    };
  }, []);

  const content = renderBlockTree(root, shared);

  if (scopeRef.current) {
    return (
      <EventScopeProvider scope={scopeRef.current}>
        {content}
      </EventScopeProvider>
    );
  }

  return content;
}

export function renderBlockTree(
  root: AnyBlock | ResolvedAnyBlock,
  shared: BlockRendererSharedProps,
): JSX.Element {
  switch (root.$type) {
    case 'form':
      return <FormBlockRenderer block={root} {...shared} />;
    case 'table':
      return <TableBlockRenderer block={root} {...shared} />;
    case 'view':
      return <ViewBlockRenderer block={root} {...shared} />;
    case 'tab':
      return <TabBlockRenderer block={root as import('../types/blocks').ResolvedTabBlock} renderBlock={renderBlockTree} {...shared} />;
    case 'wizard':
      return <WizardBlockRenderer block={root as import('../types/blocks').ResolvedWizardBlock} renderBlock={renderBlockTree} {...shared} />;
    case 'stack':
      return <StackBlockRenderer block={root as import('../types/blocks').ResolvedStackBlock} renderBlock={renderBlockTree} {...shared} />;
    case 'accordion':
      return <AccordionBlockRenderer block={root as import('../types/blocks').ResolvedAccordionBlock} renderBlock={renderBlockTree} {...shared} />;
    case 'action':
      return <ActionBlockRenderer block={root} {...shared} />;
    case 'button':
      return <ButtonBlockRenderer block={root} />;
    case 'slot':
      return root.render();
    default: {
      const _exhaustive: never = root;
      return (
        <EmptyState
          icon='error'
          title='Unsupported block type'
          description={`Unknown block type: ${(_exhaustive as AnyBlock).$type}`}
          semanticId='controlRendererUnknownBlock'
        />
      );
    }
  }
}

/**
 * Provides an EventScope for `actionId`-driven ControlRenderer paths (BUILD_* modals).
 * Without this, blocks inside builder-returned trees have no EventScope — all handles,
 * bus subscriptions, and declarative wiring are dead.
 */
function ActionIdScopeBoundary({ actionId, children }: { actionId: string; children: ReactNode }): JSX.Element {
  const scopeRef = useRef<EventScope | null>(null);

  if (!scopeRef.current) {
    scopeRef.current = new EventScope(`action://${actionId}`);
  }

  useEffect(() => {
    return () => {
      scopeRef.current?.dispose();
      scopeRef.current = null;
    };
  }, []);

  return (
    <EventScopeProvider scope={scopeRef.current}>
      {children}
    </EventScopeProvider>
  );
}

/**
 * Recursively walks a block tree and expands any colocated `wire` declarations
 * into full `EffectDeclaration` objects using `expandWire`.
 */
function collectWireEffects(block: AnyBlock | ResolvedAnyBlock): EffectDeclaration[] {
  const effects: EffectDeclaration[] = [];

  function walk(node: unknown): void {
    if (!node || typeof node !== 'object') return;
    const b = node as Record<string, unknown>;

    if (b.wire && b.semanticId) {
      effects.push(...expandWire(
        b.semanticId as string,
        b.wire as BlockWire,
        (b.$type as string | undefined) as import('@packages/block-events/core/constants').BlockType | undefined,
      ));
    }

    // Inline effects array (e.g. on StackBlock) — supports cross-block wiring
    // where source and target are different blocks not expressible via `wire`.
    if (b.effects && Array.isArray(b.effects)) {
      effects.push(...(b.effects as EffectDeclaration[]));
    }

    if (b.blocks && Array.isArray(b.blocks)) {
      for (const child of b.blocks) walk(child);
    }
    if (b.tabs && Array.isArray(b.tabs)) {
      for (const tab of b.tabs) walk((tab as Record<string, unknown>).block);
    }
    if (b.steps && Array.isArray(b.steps)) {
      for (const step of b.steps) walk((step as Record<string, unknown>).block);
    }
    if (b.sections && Array.isArray(b.sections)) {
      for (const section of b.sections) walk((section as Record<string, unknown>).block);
    }
  }

  walk(block);
  return effects;
}

setControlRendererRef(ControlRenderer);
